// @flow

import React, { Component } from 'react';
import Button from 'antd/lib/button';
import qs from 'query-string';
import { Link, navigate } from '@reach/router';
import notification from 'antd/lib/notification';
import isEmpty from 'lodash/isEmpty';
import { connect } from 'react-redux';

import type {
  ListState,
  MeasuringDeviceCertification,
  UserAccess,
  Vehicle,
} from './../../../lib/types';
import { Section } from './../../../components/layout';
import { Popconfirm, ButtonsRow, Icon, Table } from './../../../components/ui';
import { notificationLoading } from './../../../components/Notifications';
import Header from './../../../components/layout/Header';
import {
  convertVehicleToString,
  formatDateTimeToString,
  getListInitialState,
  setQueryParams,
} from './../../../lib/helpers';
import {
  type FetchListParams,
  measuringDeviceCertificationApi,
} from './../../../lib/api';
import { withUserAccess } from './../../withUserAccess';

import type { PersistFilterPayload } from '../../../ducks/persistFilters';
import { setFilter } from '../../../ducks/persistFilters';
import type { AppState } from '../../../ducks/redux';

import { ACT_COLUMNS, canHandleService } from '../lib';

import type { MeasuringDeviceCertificationsFilterParams } from './Filter';
import MeasuringDeviceCertificationsFilter from './Filter';

type Props = {
  location: Location & { state: { page: number } },
  userAccess: UserAccess[],
  persistFilters: any,
  setFilter: (payload: PersistFilterPayload) => void,
};
type State = ListState<MeasuringDeviceCertification> & {
  filterPath: string,
};

export const COLUMNS = [
  {
    title: 'Марка',
    sorter: true,
    dataIndex: 'vehicle',
    render: (vehicle: Vehicle) => convertVehicleToString(vehicle),
  },
  {
    title: 'Дата проведения последней тарировки',
    dataIndex: 'maintenanceDate',
    width: '150px',
    render: (value: string) => formatDateTimeToString(value, 'DD.MM.YYYY'),
  },
  {
    title: 'Очередная дата проведения тарировки',
    dataIndex: 'nextActDate',
    width: '150px',
    render: (value: string) => formatDateTimeToString(value, 'DD.MM.YYYY'),
  },
  {
    title: 'Сумма оплаты в руб. без НДС',
    dataIndex: 'paymentAmount',
  },
];

export class List extends Component<Props, State> {
  static defaultProps = {
    location: {},
  };
  state = {
    ...getListInitialState(),
    filterPath: window.location.pathname,
  };
  columns = [...COLUMNS, ...ACT_COLUMNS];

  async componentDidMount() {
    const { page, ...filter } = qs.parse(window.location.search);
    if (!isEmpty(filter)) {
      await this.setPersistFilter(filter);
    }
    if (this.canAdd()) {
      this.columns.push({
        stopPropagation: true,
        width: 20,
        dataIndex: 'id',
        render: (id: number) => (
          <Popconfirm
            title="Вы уверены, что хотите удалить запись?"
            onConfirm={async () => this.deleteMeasuringDeviceCertification(id)}
          >
            <Icon type="x" />
          </Popconfirm>
        ),
      });
    }
    await this.fetchMeasuringDeviceCertificationList(page);
  }

  deleteMeasuringDeviceCertification = async (id: number) => {
    try {
      notificationLoading({
        message: 'Удаление...',
        key: 'deleting',
      });
      await measuringDeviceCertificationApi.deleteMeasuringDeviceCertification(
        id
      );
      await this.fetchMeasuringDeviceCertificationList(this.state.page);
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: error.message,
      });
    } finally {
      notification.close('deleting');
    }
  };

  fetchMeasuringDeviceCertificationList = async (
    page: number = 1,
    params: FetchListParams<> = {}
  ) => {
    const { filterPath } = this.state;
    const filter = this.props.persistFilters[filterPath] || {};
    try {
      this.setState({ loading: true });
      const { data, totalCount } =
        await measuringDeviceCertificationApi.fetchMeasuringDeviceCertifications(
          {
            ...filter,
            page,
            ...params,
          }
        );
      setQueryParams({ page });
      this.setState({ data, totalCount, page });
    } catch (err) {
      notification.error({
        message: 'Ошибка',
        description: err.message,
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleRowClick = (id: number) =>
    navigate(`/services/measuring-device-certifications/${id}`);

  canAdd = () => canHandleService(this.props.userAccess);

  setPersistFilter = async (
    values: MeasuringDeviceCertificationsFilterParams
  ) => {
    const { filterPath } = this.state;
    await this.props.setFilter({
      path: filterPath,
      values,
    });
  };

  applyFilter = async (
    filter: MeasuringDeviceCertificationsFilterParams = {}
  ) => {
    await this.setPersistFilter(filter);
    await this.fetchMeasuringDeviceCertificationList();
  };

  cleanFilter = this.applyFilter;

  handlePrint = async () => {
    try {
      const filter = this.props.persistFilters[this.state.filterPath];
      notificationLoading({
        message: 'Загрузка файла отчета...',
        key: 'print',
      });
      await measuringDeviceCertificationApi.printReport(filter);
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: error.message,
      });
    } finally {
      notification.close('print');
    }
  };

  render() {
    const { location } = this.props;
    const { data, totalCount, pageSize, page, loading, filterPath } =
      this.state;
    const filter = this.props.persistFilters[filterPath];
    return (
      <>
        <Header
          left={<h1>Тарировка цистерн</h1>}
          right={
            <ButtonsRow>
              <Button onClick={this.handlePrint}>Печать</Button>
              {this.canAdd() && (
                <Link to="/services/measuring-device-certifications/new">
                  <Button type="primary" data-cy="add">
                    Создать
                  </Button>
                </Link>
              )}
            </ButtonsRow>
          }
        />
        <Section>
          <MeasuringDeviceCertificationsFilter
            filter={filter}
            applyFilter={this.applyFilter}
            cleanFilter={this.cleanFilter}
          />
          <Table
            onRow={(record) => ({
              onClick: () => this.handleRowClick(record.id),
            })}
            fetch={this.fetchMeasuringDeviceCertificationList}
            pagination={{
              page,
              pageSize,
              totalCount,
              location,
            }}
            loading={loading}
            columns={this.columns}
            data={data.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />
        </Section>
      </>
    );
  }
}

export default connect(
  (state: AppState) => ({
    persistFilters: state.persistFilters,
  }),
  {
    setFilter,
  }
)(withUserAccess(List));
